import React, { useState, useRef, useEffect } from 'react';
import { HDate } from '@hebcal/core';
import { PRIMARY_COLOR, PRIMARY_COLOR_LIGHT, PRIMARY_COLOR_DARK } from '../constants/colors';

const JewishCalendarOrrery = () => {
  // Use site colors with variations for different months
  const HEBREW_MONTHS = [
    { name: 'Tishrei', theme: 'New Beginnings', color: PRIMARY_COLOR, 
      holidays: [
        { name: 'Rosh Hashanah', dayInMonth: 1 },
        { name: 'Yom Kippur', dayInMonth: 10 },
        { name: 'Sukkot', dayInMonth: 15 }
      ]
    },
    { name: 'Cheshvan', theme: 'Ordinary Time', color: '#717171', holidays: [] },
    { name: 'Kislev', theme: 'Light in Darkness', color: PRIMARY_COLOR_DARK, 
      holidays: [{ name: 'Chanukah', dayInMonth: 25 }]
    },
    { name: 'Tevet', theme: 'Winter Depth', color: '#222222', holidays: [] },
    { name: 'Shvat', theme: 'Nature Awakening', color: PRIMARY_COLOR_LIGHT, 
      holidays: [{ name: 'Tu B\'Shvat', dayInMonth: 15 }]
    },
    { name: 'Adar', theme: 'Joy & Celebration', color: PRIMARY_COLOR, 
      holidays: [{ name: 'Purim', dayInMonth: 14 }]
    },
    { name: 'Nisan', theme: 'Freedom & Renewal', color: PRIMARY_COLOR_DARK, 
      holidays: [{ name: 'Passover', dayInMonth: 15 }]
    },
    { name: 'Iyar', theme: 'Transition', color: PRIMARY_COLOR_LIGHT, 
      holidays: [{ name: 'Lag B\'Omer', dayInMonth: 18 }]
    },
    { name: 'Sivan', theme: 'Revelation', color: PRIMARY_COLOR, 
      holidays: [{ name: 'Shavuot', dayInMonth: 6 }]
    },
    { name: 'Tammuz', theme: 'Difficulty', color: '#717171', holidays: [] },
    { name: 'Av', theme: 'Mourning to Joy', color: PRIMARY_COLOR_DARK, 
      holidays: [{ name: 'Tisha B\'Av', dayInMonth: 9 }]
    },
    { name: 'Elul', theme: 'Introspection', color: PRIMARY_COLOR_LIGHT, holidays: [] }
  ];

  // Normalize angle to 0-360 range (infinite loop)
  // Must be defined before calculateInitialAngle since it's used there
  const normalizeAngle = (angle) => {
    let normalized = angle % 360;
    if (normalized < 0) normalized += 360;
    return normalized;
  };

  // Calculate initial angle based on today's Hebrew date
  // Angle 0 = 1 Tishrei, angle increases clockwise
  const calculateInitialAngle = () => {
    try {
      const today = new HDate(); // This gets TODAY'S Hebrew date
      console.log('Today HDate:', today.toString()); // Debug
      
      const hebrewMonth = today.getMonth(); // 1-12 where 1=Nisan, 7=Tishrei
      const hebrewDay = today.getDate();
      const daysInMonth = today.daysInMonth();
      
      console.log('Hebrew month:', hebrewMonth, 'Day:', hebrewDay, 'Days in month:', daysInMonth);
      
      // Map Hebrew calendar month (1=Nisan) to our array (0=Tishrei)
      const monthMap = {
        7: 0,   // Tishrei
        8: 1,   // Cheshvan
        9: 2,   // Kislev
        10: 3,  // Tevet -> Should be HERE today (Jan 9, 2026)
        11: 4,  // Shvat
        12: 5,  // Adar
        1: 6,   // Nisan
        2: 7,   // Iyar
        3: 8,   // Sivan
        4: 9,   // Tammuz
        5: 10,  // Av
        6: 11   // Elul
      };
      
      const monthIndex = monthMap[hebrewMonth];
      console.log('Mapped month index:', monthIndex);
      
      if (monthIndex === undefined) {
        console.error('Unknown month:', hebrewMonth, 'for date:', today.toString());
        return 0;
      }
      
      // Calculate angle
      const monthAngle = monthIndex * 30;
      const dayFraction = (hebrewDay - 1) / daysInMonth;
      const dayAngle = dayFraction * 30;
      const totalAngle = monthAngle + dayAngle;
      
      console.log('Angle calculation:', { monthAngle, dayFraction, dayAngle, totalAngle });
      
      return normalizeAngle(totalAngle);
    } catch (error) {
      console.error('Error calculating initial angle:', error);
      return 0;
    }
  };

  const [currentAngle, setCurrentAngle] = useState(() => calculateInitialAngle());
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0, angle: 0 });
  const containerRef = useRef(null);
  const svgRef = useRef(null);

  const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;
  // Smaller sizes for mobile optimization
  const size = isMobile ? Math.min(280, window.innerWidth - 32) : 400;
  const centerX = size / 2;
  const centerY = size / 2;
  const sunSize = isMobile ? 24 : 32;
  const orbitRadius = isMobile ? 80 : 110; // Much smaller orbit
  const earthRadius = isMobile ? 10 : 14;
  const moonRadius = isMobile ? 4 : 6;
  const moonOrbitRadius = isMobile ? 16 : 22;

  const getPosition = (angle, radius) => ({
    x: centerX + radius * Math.cos(angle),
    y: centerY + radius * Math.sin(angle)
  });

  const degreesToRadians = (degrees) => degrees * Math.PI / 180;
  const radiansToDegrees = (radians) => radians * 180 / Math.PI;

  // Get current month index (0-11), handling infinite loop
  const getCurrentMonth = () => {
    const normalized = normalizeAngle(currentAngle);
    const monthIndex = Math.floor(normalized / 30);
    return monthIndex >= 12 ? monthIndex % 12 : monthIndex;
  };

  const earthAngle = degreesToRadians(normalizeAngle(currentAngle) - 90);
  const earthPos = getPosition(earthAngle, orbitRadius);

  // Calculate angle from sun to earth (for determining bright/dark side)
  // The bright side faces the sun, so we need the angle from earth to sun
  const earthToSunAngle = Math.atan2(centerY - earthPos.y, centerX - earthPos.x);

  // Moon orbits earth 12 times counter-clockwise per year
  const moonLocalAngle = degreesToRadians(-normalizeAngle(currentAngle) * 12);
  const moonAbsoluteAngle = earthAngle + Math.PI + moonLocalAngle;
  const moonOffset = getPosition(moonAbsoluteAngle, moonOrbitRadius);
  const moonPos = {
    x: earthPos.x + moonOffset.x - centerX,
    y: earthPos.y + moonOffset.y - centerY
  };

  // Calculate angle from moon to sun (for determining bright/dark side)
  const moonToSunAngle = Math.atan2(centerY - moonPos.y, centerX - moonPos.x);

  const currentMonth = getCurrentMonth();
  const currentMonthData = HEBREW_MONTHS[currentMonth];

  // Calculate current Hebrew date from angle
  // When earth is at top (angle 0), it's 1 Tishrei
  // As earth moves clockwise, days increase
  // Each month is 30 degrees, and moon completes 12 revolutions per year
  const getCurrentHebrewDate = () => {
    try {
      const normalized = normalizeAngle(currentAngle);
      
      // Get current Hebrew year (should stay constant as we scrub through months)
      const today = new HDate();
      const hebrewYear = today.getFullYear();
      
      // Calculate month index from angle
      // Our array: Tishrei(0), Cheshvan(1), Kislev(2), Tevet(3), Shvat(4), Adar(5), Nisan(6), Iyar(7), Sivan(8), Tammuz(9), Av(10), Elul(11)
      const monthIndex = Math.floor(normalized / 30) % 12;
      
      // Map our array index back to Hebrew month number (1-indexed for HDate)
      const reverseMonthMap = {
        0: 7,   // Tishrei -> 7
        1: 8,   // Cheshvan -> 8
        2: 9,   // Kislev -> 9
        3: 10,  // Tevet -> 10
        4: 11,  // Shvat -> 11
        5: 12,  // Adar -> 12
        6: 1,   // Nisan -> 1
        7: 2,   // Iyar -> 2
        8: 3,   // Sivan -> 3
        9: 4,   // Tammuz -> 4
        10: 5,  // Av -> 5
        11: 6   // Elul -> 6
      };
      
      const hebrewMonth = reverseMonthMap[monthIndex];
      if (hebrewMonth === undefined) {
        console.error('Invalid monthIndex:', monthIndex);
        return { hebrew: '', english: '', hDate: null };
      }
      
      // Calculate day in month
      // The angle within the current month is: normalized - (monthIndex * 30)
      const angleInMonth = normalized - (monthIndex * 30);
      const dayFraction = angleInMonth / 30; // 0 to 1, where 0 = day 1, 1 = last day
      
      console.log('Date calculation:', { normalized, monthIndex, hebrewMonth, angleInMonth, dayFraction });
      
      // Get days in this month
      // HDate constructor: new HDate(year, month, day) where month is 1-12
      let monthStart;
      try {
        monthStart = new HDate(hebrewYear, hebrewMonth, 1);
      } catch (e) {
        console.error('Error creating month start:', e, { hebrewYear, hebrewMonth });
        const today = new HDate();
        monthStart = new HDate(today.getFullYear(), hebrewMonth, 1);
      }
      const daysInMonth = monthStart.daysInMonth();
      
      // Convert dayFraction to day number
      // dayFraction 0 = day 1, dayFraction close to 1 = last day
      // Use Math.floor and add 1 to ensure day 1 when dayFraction is 0
      const dayInMonth = Math.max(1, Math.min(daysInMonth, Math.floor(dayFraction * daysInMonth) + 1));
      
      console.log('Day calculation:', { daysInMonth, dayFraction, calculatedDay: dayInMonth });
      
      // Create the Hebrew date
      // HDate constructor: new HDate(year, month, day)
      let hDate;
      try {
        hDate = new HDate(hebrewYear, hebrewMonth, dayInMonth);
      } catch (e) {
        console.error('Error creating Hebrew date:', e, { hebrewYear, hebrewMonth, dayInMonth });
        const today = new HDate();
        hDate = new HDate(today.getFullYear(), hebrewMonth, dayInMonth);
      }
      
      console.log('Created HDate:', hDate.toString());
      
      // Format Hebrew date - use getMonthName and getDate
      const hebrewMonthName = hDate.getMonthName('h');
      const day = hDate.getDate();
      const formattedHebrew = `${day} ${hebrewMonthName}`;
      
      // Convert to Gregorian using HDate's method
      const gregorianDate = hDate.greg();
      const formattedEnglish = gregorianDate.toLocaleDateString('en-US', { 
        month: 'short', 
        day: 'numeric', 
        year: 'numeric' 
      });
      
      console.log('Final dates:', { hebrew: formattedHebrew, english: formattedEnglish });
      
      return { 
        hebrew: formattedHebrew, 
        english: formattedEnglish,
        hDate: hDate
      };
    } catch (error) {
      console.error('Error calculating Hebrew date:', error);
      return { hebrew: '', english: '', hDate: null };
    }
  };

  const currentDates = getCurrentHebrewDate();

  const jumpToMonth = (monthIndex) => {
    setCurrentAngle(normalizeAngle(monthIndex * 30 + 15));
  };

  // Jump to holiday date
  const jumpToHoliday = (monthIndex, dayInMonth) => {
    const monthAngle = monthIndex * 30;
    // Approximate: assume 30 days per month for simplicity
    const dayAngle = (dayInMonth / 30) * 30;
    setCurrentAngle(normalizeAngle(monthAngle + dayAngle));
  };

  // Handle drag start - use a ref to always get the latest currentAngle
  const currentAngleRef = useRef(currentAngle);
  useEffect(() => {
    currentAngleRef.current = currentAngle;
  }, [currentAngle]);

  const handleDragStart = (clientX, clientY) => {
    setIsDragging(true);
    setDragStart({
      x: clientX,
      y: clientY,
      angle: normalizeAngle(currentAngleRef.current)
    });
  };

  // Handle drag move - HORIZONTAL drag only
  const handleDragMove = (clientX, clientY) => {
    if (!isDragging) return;
    
    const deltaX = clientX - dragStart.x;
    
    // Calculate angle change based on HORIZONTAL drag only
    const angleDelta = deltaX * 0.5;
    
    const newAngle = normalizeAngle(dragStart.angle + angleDelta);
    setCurrentAngle(newAngle);
  };

  // Handle drag end
  const handleDragEnd = () => {
    setIsDragging(false);
  };

  // Mouse events
  useEffect(() => {
    const handleMouseDown = (e) => {
      if (e.button === 0) { // Left mouse button
        handleDragStart(e.clientX, e.clientY);
      }
    };

    const handleMouseMove = (e) => {
      if (isDragging) {
        handleDragMove(e.clientX, e.clientY);
      }
    };

    const handleMouseUp = () => {
      if (isDragging) {
        handleDragEnd();
      }
    };

    if (containerRef.current) {
      containerRef.current.addEventListener('mousedown', handleMouseDown);
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      if (containerRef.current) {
        containerRef.current.removeEventListener('mousedown', handleMouseDown);
      }
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging]);

  // Touch events
  useEffect(() => {
    const handleTouchStart = (e) => {
      if (e.touches.length === 1) {
        const touch = e.touches[0];
        handleDragStart(touch.clientX, touch.clientY);
      }
    };

    const handleTouchMove = (e) => {
      if (isDragging && e.touches.length === 1) {
        e.preventDefault(); // Prevent scrolling
        const touch = e.touches[0];
        handleDragMove(touch.clientX, touch.clientY);
      }
    };

    const handleTouchEnd = () => {
      if (isDragging) {
        handleDragEnd();
      }
    };

    if (containerRef.current) {
      containerRef.current.addEventListener('touchstart', handleTouchStart, { passive: false });
      containerRef.current.addEventListener('touchmove', handleTouchMove, { passive: false });
      containerRef.current.addEventListener('touchend', handleTouchEnd);
    }

    return () => {
      if (containerRef.current) {
        containerRef.current.removeEventListener('touchstart', handleTouchStart);
        containerRef.current.removeEventListener('touchmove', handleTouchMove);
        containerRef.current.removeEventListener('touchend', handleTouchEnd);
      }
    };
  }, [isDragging]);


  // Calculate which half of the circle faces the sun
  // We'll create a path for the bright side (the half facing the sun)
  const getBrightSidePath = (centerX, centerY, radius, toSunAngle) => {
    // The bright side is the half that faces the sun
    // The sun is at angle toSunAngle from the object
    // The bright semicircle should be centered on toSunAngle
    // So it goes from toSunAngle - π/2 to toSunAngle + π/2
    const startAngle = toSunAngle - Math.PI / 2;
    const endAngle = toSunAngle + Math.PI / 2;
    
    // Draw the semicircle that contains the sun (sweep clockwise)
    const sweepFlag = 1;
    
    const startX = centerX + Math.cos(startAngle) * radius;
    const startY = centerY + Math.sin(startAngle) * radius;
    const endX = centerX + Math.cos(endAngle) * radius;
    const endY = centerY + Math.sin(endAngle) * radius;
    
    return `M ${startX} ${startY} A ${radius} ${radius} 0 0 ${sweepFlag} ${endX} ${endY} L ${centerX} ${centerY} Z`;
  };

  return (
    <div 
      ref={containerRef}
      className="min-h-screen bg-background flex flex-col items-center pt-14 md:pt-8 pb-8 md:pb-12 px-4 md:px-8"
      style={{ 
        cursor: isDragging ? 'grabbing' : 'grab',
        userSelect: 'none',
        touchAction: 'none'
      }}
    >
      <div className="max-w-4xl w-full">
        <h1 className="text-2xl md:text-4xl font-semibold text-text-primary mb-2 text-center" style={{ letterSpacing: '-0.02em' }}>
          Jewish Calendar
        </h1>
        
        {/* Current dates */}
        <div className="text-center mb-4 md:mb-6">
          <div className="text-base md:text-lg font-semibold text-text-primary mb-1">
            {currentDates.hebrew}
          </div>
          <div className="text-sm md:text-base text-text-secondary">
            {currentDates.english}
          </div>
        </div>

        <p className="text-xs md:text-base text-text-secondary mb-4 md:mb-8 text-center">
          Drag anywhere to navigate through the year
        </p>

        <div className="mb-4 md:mb-6 relative">
          <svg ref={svgRef} width={size} height={size} style={{ display: 'block', margin: '0 auto' }}>
            {/* Sun - Star shape */}
            <g transform={`translate(${centerX - sunSize / 2}, ${centerY - sunSize / 2}) scale(${sunSize / 93})`}>
              <svg width="93" height="97" viewBox="0 0 93 97" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M42.719 1.23596C44.8714 -0.412002 47.8615 -0.412 50.0139 1.23596L57.6948 7.11664C58.7828 7.94962 60.1231 8.38512 61.4929 8.35072L71.1634 8.10784C73.8734 8.03978 76.2924 9.7973 77.0651 12.3957L79.8225 21.668C80.2131 22.9814 81.0414 24.1215 82.1699 24.8988L90.1362 30.3865C92.3687 31.9244 93.2927 34.7681 92.3905 37.3244L89.1711 46.4466C88.7151 47.7387 88.7151 49.148 89.1711 50.4401L92.3905 59.5623C93.2927 62.1186 92.3687 64.9624 90.1362 66.5002L82.1699 71.9879C81.0414 72.7652 80.2131 73.9054 79.8225 75.2188L77.0651 84.491C76.2924 87.0894 73.8734 88.8469 71.1634 88.7789L61.4929 88.536C60.1231 88.5016 58.7828 88.9371 57.6948 89.7701L50.0139 95.6508C47.8615 97.2987 44.8714 97.2987 42.719 95.6508L35.0381 89.7701C33.9502 88.9371 32.6098 88.5016 31.24 88.536L21.5695 88.7789C18.8595 88.8469 16.4405 87.0894 15.6678 84.491L12.9104 75.2188C12.5198 73.9054 11.6915 72.7652 10.563 71.9879L2.59669 66.5002C0.364241 64.9624 -0.559744 62.1186 0.342424 59.5623L3.56176 50.4401C4.01778 49.148 4.01778 47.7387 3.56176 46.4466L0.342423 37.3244C-0.559745 34.7681 0.364239 31.9244 2.59668 30.3865L10.563 24.8988C11.6915 24.1215 12.5198 22.9814 12.9104 21.668L15.6678 12.3957C16.4405 9.7973 18.8595 8.03978 21.5695 8.10784L31.24 8.35072C32.6098 8.38512 33.9502 7.94962 35.0381 7.11664L42.719 1.23596Z" fill={PRIMARY_COLOR}/>
              </svg>
            </g>

            {/* Orbit */}
            <circle 
              cx={centerX} 
              cy={centerY} 
              r={orbitRadius} 
              fill="none" 
              stroke="#DDDDDD"
              strokeWidth="1.5"
              strokeDasharray="6,6"
            />

            {/* Month arcs and markers */}
            {HEBREW_MONTHS.map((month, index) => {
              const startAngle = -Math.PI / 2 + (index * 2 * Math.PI / 12);
              const endAngle = startAngle + (2 * Math.PI / 12);
              const startPos = getPosition(startAngle, orbitRadius);
              const endPos = getPosition(endAngle, orbitRadius);
              const midAngle = startAngle + Math.PI / 12;
              const labelPos = getPosition(midAngle, orbitRadius + (isMobile ? 30 : 45));
              const isActive = index === currentMonth;

              return (
                <g key={month.name}>
                  {/* Month arc */}
                  <path
                    d={`M ${startPos.x} ${startPos.y} A ${orbitRadius} ${orbitRadius} 0 0 1 ${endPos.x} ${endPos.y}`}
                    fill="none"
                    stroke={isActive ? month.color : '#DDDDDD'}
                    strokeWidth={isActive ? (isMobile ? 3 : 4) : (isMobile ? 1.5 : 2)}
                    opacity={isActive ? 1 : 0.3}
                    style={{ cursor: 'pointer', transition: 'all 0.3s ease' }}
                    onClick={(e) => {
                      e.stopPropagation();
                      jumpToMonth(index);
                    }}
                  />

                  {/* Month label */}
                  <text
                    x={labelPos.x}
                    y={labelPos.y}
                    fill={isActive ? month.color : '#717171'}
                    fontSize={isActive ? (isMobile ? 10 : 14) : (isMobile ? 8 : 11)}
                    fontWeight={isActive ? '600' : '500'}
                    textAnchor="middle"
                    style={{ cursor: 'pointer', userSelect: 'none', transition: 'all 0.3s ease', fontFamily: 'DM Sans, sans-serif' }}
                    onClick={(e) => {
                      e.stopPropagation();
                      jumpToMonth(index);
                    }}
                  >
                    {month.name}
                    {isActive && (
                      <tspan
                        x={labelPos.x}
                        dy="1.2em"
                        fontSize={isMobile ? 7 : 10}
                        fontWeight="400"
                        fill={month.color}
                        opacity="0.8"
                      >
                        {month.theme}
                      </tspan>
                    )}
                  </text>

                  {/* Holiday markers */}
                  {month.holidays.map((holiday, holidayIndex) => {
                    const holidayAngle = startAngle + (holiday.dayInMonth / 30) * (2 * Math.PI / 12);
                    const holidayPos = getPosition(holidayAngle, orbitRadius);
                    // Stagger holiday labels to prevent overlap - alternate above and below
                    const baseOffset = isMobile ? 12 : 18;
                    const staggerOffset = holidayIndex % 2 === 0 ? baseOffset : -baseOffset;
                    const holidayLabelPos = getPosition(holidayAngle, orbitRadius + staggerOffset);
                    
                    return (
                      <g 
                        key={holiday.name}
                        onClick={(e) => {
                          e.stopPropagation();
                          jumpToHoliday(index, holiday.dayInMonth);
                        }}
                        style={{ cursor: 'pointer' }}
                      >
                        <circle
                          cx={holidayPos.x}
                          cy={holidayPos.y}
                          r={isMobile ? 3 : 4}
                          fill={month.color}
                          stroke="#fff"
                          strokeWidth="1.5"
                        />
                        <text
                          x={holidayLabelPos.x}
                          y={holidayLabelPos.y}
                          fill={month.color}
                          fontSize={isMobile ? 7 : 9}
                          fontWeight="500"
                          textAnchor="middle"
                          opacity="0.8"
                        >
                          {holiday.name}
                        </text>
                      </g>
                    );
                  })}
                </g>
              );
            })}

            {/* Earth with bright/dark sides */}
            <g>
              {/* Full circle as dark side base */}
              <circle 
                cx={earthPos.x} 
                cy={earthPos.y} 
                r={earthRadius} 
                fill="#000000"
              />
              {/* Bright side (facing sun) - drawn as a semicircle path */}
              <path
                d={getBrightSidePath(earthPos.x, earthPos.y, earthRadius, earthToSunAngle)}
                fill={PRIMARY_COLOR_DARK}
              />
            </g>

            {/* Moon with bright/dark sides */}
            <g>
              {/* Full circle as dark side base */}
              <circle 
                cx={moonPos.x} 
                cy={moonPos.y} 
                r={moonRadius} 
                fill="#000000"
              />
              {/* Bright side (facing sun) - drawn as a semicircle path */}
              <path
                d={getBrightSidePath(moonPos.x, moonPos.y, moonRadius, moonToSunAngle)}
                fill="#F7F7F7"
              />
            </g>
          </svg>
        </div>
      </div>
    </div>
  );
};

export default JewishCalendarOrrery;
